import { useServices } from '@/app/providers';
import { downloadBlob } from '@/core/utils/download';
import type { BulkOperation, BulkOperationResource } from '@yawi/types';
import {
  Download,
  FileText,
  History,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileUploadArea } from './FileUploadArea';
import { ImportResults } from './ImportResults';

interface BulkImportTabProps {
  tenantId: string;
}

const RESOURCES: { value: BulkOperationResource; label: string; description: string }[] = [
  { value: 'INITIAL_SETUP', label: 'Configuración Inicial', description: 'Unidades, residentes y amenidades base' },
  { value: 'RESIDENTS', label: 'Residentes', description: 'Importar residentes al tenant' },
  { value: 'AMENITIES', label: 'Amenidades', description: 'Espacios comunes y áreas' },
  { value: 'PAYMENTS', label: 'Pagos', description: 'Historial de pagos' },
  { value: 'USERS', label: 'Usuarios', description: 'Cuentas de usuario' },
];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Pendiente', color: 'bg-slate-100 text-slate-700' },
  PROCESSING: { label: 'Procesando', color: 'bg-yellow-100 text-yellow-700' },
  COMPLETED: { label: 'Completado', color: 'bg-green-100 text-green-700' },
  FAILED: { label: 'Fallido', color: 'bg-red-100 text-red-700' },
  CANCELLED: { label: 'Cancelado', color: 'bg-slate-100 text-slate-500' },
};

const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);

export function BulkImportTab({ tenantId }: BulkImportTabProps) {
  const { bulkOperationRepository } = useServices();

  const [selectedResource, setSelectedResource] = useState<BulkOperationResource>('INITIAL_SETUP');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [activeOperation, setActiveOperation] = useState<BulkOperation | null>(null);
  const [history, setHistory] = useState<BulkOperation[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedHistoryOp, setSelectedHistoryOp] = useState<BulkOperation | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      const result = await bulkOperationRepository.listOperations(tenantId, {
        type: 'IMPORT',
        limit: 20,
        offset: 0,
      });
      setHistory(result.data);
    } catch {
      // silently fail
    }
  }, [bulkOperationRepository, tenantId]);

  useEffect(() => {
    setIsLoadingHistory(true);
    refreshHistory().finally(() => setIsLoadingHistory(false));
  }, [refreshHistory]);

  useEffect(() => {
    if (!activeOperation || TERMINAL_STATUSES.has(activeOperation.status)) {
      stopPolling();
      return;
    }
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const updated = await bulkOperationRepository.getOperation(activeOperation.id);
        setActiveOperation(updated);
        if (TERMINAL_STATUSES.has(updated.status)) {
          stopPolling();
          refreshHistory();
        }
      } catch {
        stopPolling();
      }
    }, 3000);
    return stopPolling;
  }, [activeOperation?.id, activeOperation?.status, bulkOperationRepository, stopPolling, refreshHistory]);

  async function handleDownloadTemplate() {
    setIsDownloading(true);
    try {
      const blob = await bulkOperationRepository.downloadTemplate(selectedResource);
      downloadBlob(blob, `plantilla-${selectedResource.toLowerCase()}.xlsx`);
    } catch {
      // silently fail — toast would require useToast integration
    } finally {
      setIsDownloading(false);
    }
  }

  async function handleImport() {
    if (!selectedFile) return;
    setIsUploading(true);
    setActiveOperation(null);
    setSelectedHistoryOp(null);
    try {
      const op = await bulkOperationRepository.importFile(
        selectedFile,
        selectedResource,
        tenantId,
        { dryRun, updateExisting },
      );
      setActiveOperation(op);
      setSelectedFile(null);
    } catch {
      // silently fail
    } finally {
      setIsUploading(false);
    }
  }

  async function handleCancel() {
    if (!activeOperation) return;
    try {
      const updated = await bulkOperationRepository.cancelOperation(activeOperation.id);
      setActiveOperation(updated);
      stopPolling();
      refreshHistory();
    } catch {
      // silently fail
    }
  }

  const isOperationActive =
    activeOperation &&
    !TERMINAL_STATUSES.has(activeOperation.status);

  const isOperationComplete =
    activeOperation &&
    TERMINAL_STATUSES.has(activeOperation.status);

  return (
    <div className="space-y-6">
      {/* New import form */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Upload className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-base font-semibold text-theme">Nueva importación</h3>
        </div>

        <div className="p-6 space-y-5">
          {/* Resource selector */}
          <div>
            <label className="block text-sm font-medium text-theme mb-2">Tipo de recurso</label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {RESOURCES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setSelectedResource(r.value)}
                  disabled={isUploading || !!isOperationActive}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${
                    selectedResource === r.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <p className={`text-sm font-medium ${selectedResource === r.value ? 'text-blue-700' : 'text-theme'}`}>
                    {r.label}
                  </p>
                  <p className="text-xs text-theme-secondary mt-0.5">{r.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Template download */}
          <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-theme">Plantilla</p>
              <p className="text-xs text-theme-secondary">
                Descarga la plantilla para {RESOURCES.find((r) => r.value === selectedResource)?.label}
              </p>
            </div>
            <button
              onClick={handleDownloadTemplate}
              disabled={isDownloading || isUploading || !!isOperationActive}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-slate-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Descargar
            </button>
          </div>

          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-theme mb-2">Archivo</label>
            <FileUploadArea
              onFileSelected={setSelectedFile}
              selectedFile={selectedFile}
              onClear={() => setSelectedFile(null)}
              disabled={isUploading || !!isOperationActive}
            />
          </div>

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={isUploading || !!isOperationActive}
                className="w-4 h-4 rounded border-slate-300 accent-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-theme">Simulación</span>
                <p className="text-xs text-theme-secondary">No crear datos reales</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                disabled={isUploading || !!isOperationActive}
                className="w-4 h-4 rounded border-slate-300 accent-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-theme">Actualizar existentes</span>
                <p className="text-xs text-theme-secondary">Sobreescribir registros duplicados</p>
              </div>
            </label>
          </div>

          {/* Submit */}
          <button
            onClick={handleImport}
            disabled={!selectedFile || isUploading || !!isOperationActive}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Subiendo...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Iniciar importación
              </>
            )}
          </button>
        </div>
      </div>

      {/* Active operation status */}
      {activeOperation && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOperationActive ? (
                <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
              ) : null}
              <h3 className="text-base font-semibold text-theme">Estado de la operación</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_LABELS[activeOperation.status]?.color ?? 'bg-slate-100 text-slate-700'}`}>
                {STATUS_LABELS[activeOperation.status]?.label ?? activeOperation.status}
              </span>
              {activeOperation.status === 'PENDING' && (
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <X className="w-3 h-3" />
                  Cancelar
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {isOperationActive ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                <p className="text-sm text-theme-secondary">
                  {activeOperation.status === 'PENDING'
                    ? 'En cola, esperando procesamiento...'
                    : 'Procesando importación...'}
                </p>
                {activeOperation.fileName && (
                  <p className="text-xs text-theme-tertiary">{activeOperation.fileName}</p>
                )}
              </div>
            ) : (
              <ImportResults operation={activeOperation} />
            )}
          </div>
        </div>
      )}

      {/* History operation detail */}
      {selectedHistoryOp && !activeOperation && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-theme">Detalle de operación</h3>
            <button
              onClick={() => setSelectedHistoryOp(null)}
              className="p-1 rounded-lg hover:bg-slate-100 text-theme-secondary transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-6">
            <ImportResults operation={selectedHistoryOp} />
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <History className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-base font-semibold text-theme">Historial de importaciones</h3>
        </div>

        {isLoadingHistory ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center">
            <History className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-theme-secondary">Sin importaciones anteriores</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-theme-secondary font-medium">Fecha</th>
                  <th className="text-left px-4 py-3 text-theme-secondary font-medium">Recurso</th>
                  <th className="text-left px-4 py-3 text-theme-secondary font-medium">Archivo</th>
                  <th className="text-center px-4 py-3 text-theme-secondary font-medium">Estado</th>
                  <th className="text-center px-4 py-3 text-theme-secondary font-medium">Filas</th>
                  <th className="text-right px-4 py-3 text-theme-secondary font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {history.map((op) => {
                  const resource = RESOURCES.find((r) => r.value === op.resource);
                  const status = STATUS_LABELS[op.status];
                  return (
                    <tr
                      key={op.id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-theme-secondary">
                        {new Date(op.createdAt).toLocaleDateString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 font-medium text-theme">
                        {resource?.label ?? op.resource}
                      </td>
                      <td className="px-4 py-3 text-theme-secondary truncate max-w-[160px]">
                        {op.fileName ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status?.color ?? 'bg-slate-100 text-slate-700'}`}>
                          {status?.label ?? op.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-theme-secondary">
                        {op.summary?.totalRows ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {TERMINAL_STATUSES.has(op.status) && (
                          <button
                            onClick={() => {
                              setSelectedHistoryOp(op);
                              setActiveOperation(null);
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Ver detalle
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
